import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { IconDefinition } from '#src/atoms/icons/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconCheck } from '#src/atoms/icons/system/iconCheck.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { OAuthButton } from '#src/components/user/OAuthButton/index.tsx';
import { Disconnect } from '#src/components/user/OAuthDisconnect/index.tsx';

type Props = {
  nickname: string | null;
  provider: 'discord' | 'twitter' | 'google_oauth2' | 'facebook';
  icon: IconDefinition;
};

const providerName = (provider: string) => {
  switch (provider) {
    case 'google_oauth2':
      return 'google';
    case 'twitter':
      return 'x';
    default:
      return provider;
  }
};

/*
 * The nickname is the user's email in case of OmniauthIdentities for providers
 * `facebook` and `google_oauth_2`. It is the user nickname for others.
 */
const connectedEmail = (provider: string, nickname: string) => {
  switch (provider) {
    case 'google_oauth2':
    case 'facebook':
      return nickname;
    default:
      return null;
  }
};

const Connected = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--unit);
`;
const UsernameInfo = styled(Horizontal).attrs({ gap: 2 })`
  padding: calc(1 * var(--unit)) 0;
`;
const Icons = styled.div`
  position: relative;
`;
const Icon = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  &.twitter {
    background-color: var(--c-social-x-twitter);
  }
  &.discord {
    background-color: var(--c-social-discord);
  }
  &.google_oauth2 {
    background-color: var(--c-social-google);
  }
  &.facebook {
    background-color: var(--c-social-facebook);
  }
`;
const CheckIcon = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 16px;
  height: 16px;
  position: absolute;
  right: 0;
  bottom: 0;
  background-color: var(--c-white);
  border-radius: 50%;
  border: solid 2px var(--c-black);
`;

const Social = styled(BodyM)`
  text-transform: capitalize;
`;

const Buttons = styled(Horizontal).attrs({ gap: 0 })`
  gap: 10px;
  flex-wrap: wrap;
`;

export const OAuthAccount = ({ nickname, provider, icon }: Props) => {
  return (
    <div>
      <Connected>
        <UsernameInfo>
          <Icons>
            <Icon className={provider}>
              <FontAwesomeIcon color="var(--c-white)" icon={icon} />
            </Icon>
            {!!nickname && (
              <CheckIcon>
                <FontAwesomeIcon
                  color="var(--c-black)"
                  width="8"
                  icon={iconCheck}
                />
              </CheckIcon>
            )}
          </Icons>
          {!!nickname && (
            <div>
              <Social>{providerName(provider)}</Social>
              <BodyM color="var(--c-nd-600)">{nickname}</BodyM>
            </div>
          )}
        </UsernameInfo>
        <Buttons>
          {nickname ? (
            <Disconnect
              provider={provider}
              email={connectedEmail(provider, nickname)}
            />
          ) : (
            <OAuthButton provider={provider}>
              <FormattedMessage defaultMessage="Link" />
            </OAuthButton>
          )}
        </Buttons>
      </Connected>
    </div>
  );
};
